// Panel „Kategorie nahoru" na stránce Produkt týdne: produkty jedné kategorie
// posune úplně nahoru ve všech kategoriích, kde jsou, v pořadí, které si tady
// nastavíš šipkami (výchozí = jak je teď zákazník vidí na e-shopu).

import { ApiError, apiFetch } from '@/lib/apiFetch';
import { type FormEvent, useState } from 'react';

type BoostProduct = {
  guid: string;
  hidden?: boolean;
  code: string | null;
  name: string;
  url: string | null;
  categories: { guid: string; name: string }[];
};

type CategoryResult = { guid: string; name: string; products: number; ok: boolean; error?: string };

type Props = { onToast: (msg: string, kind: 'success' | 'error') => void };

export function CategoryBoostPanel({ onToast }: Props) {
  const [url, setUrl] = useState('');
  const [products, setProducts] = useState<BoostProduct[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState<CategoryResult[] | null>(null);

  async function load(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setResults(null);
    try {
      const r = await apiFetch<{
        products: BoostProduct[];
        failed: { guid: string; status: number }[];
        truncated: boolean;
      }>('/api/kategorie-nahoru/nacist', { method: 'POST', body: { url: url.trim() } });
      setProducts(r.products);
      if (r.failed.length) {
        onToast(
          `${r.failed.length} produktů se nepodařilo načíst ze Shoptetu (HTTP ${r.failed[0]?.status}) — zkus Načíst znovu`,
          'error',
        );
      } else if (r.truncated) {
        onToast('Načteno jen prvních 100 produktů', 'error');
      }
    } catch (err: unknown) {
      setProducts(null);
      onToast(err instanceof ApiError ? err.describe() : 'Načtení selhalo', 'error');
    } finally {
      setLoading(false);
    }
  }

  function move(idx: number, dir: -1 | 1) {
    if (!products) return;
    const next = [...products];
    const target = idx + dir;
    const a = next[idx];
    const b = next[target];
    if (!a || !b) return;
    next[idx] = b;
    next[target] = a;
    setProducts(next);
  }

  // Všechny kategorie, kde se něco posune (+ kolik našich produktů v nich je).
  const categories = new Map<string, { name: string; count: number }>();
  for (const p of products ?? []) {
    for (const c of p.categories) {
      const cur = categories.get(c.guid) ?? { name: c.name, count: 0 };
      cur.count += 1;
      categories.set(c.guid, cur);
    }
  }

  async function apply() {
    if (!products?.length) return;
    if (
      !confirm(
        `Posunout ${products.length} produktů úplně nahoru v ${categories.size} kategoriích (v tomhle pořadí)?`,
      )
    ) {
      return;
    }
    setApplying(true);
    try {
      const r = await apiFetch<{ results: CategoryResult[] }>('/api/kategorie-nahoru/posunout', {
        method: 'POST',
        body: { url: url.trim(), order: products.map((p) => p.guid) },
      });
      setResults(r.results);
      const failed = r.results.filter((x) => !x.ok).length;
      onToast(
        failed
          ? `Hotovo, ale ${failed} kategorií se nepovedlo`
          : `Hotovo — posunuto v ${r.results.length} kategoriích`,
        failed ? 'error' : 'success',
      );
    } catch (err: unknown) {
      onToast(err instanceof ApiError ? err.describe() : 'Posun selhal', 'error');
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="bg-surface rounded-xl border border-border p-5 mt-8">
      <h2 className="text-lg font-bold text-text">Kategorie nahoru</h2>
      <p className="text-sm text-text-secondary mt-0.5 mb-4 max-w-2xl">
        Produkty jedné kategorie posune úplně nahoru ve všech kategoriích, kde jsou — v pořadí,
        které tu nastavíš (Produkt týdne zůstává nad nimi).
      </p>
      <form onSubmit={load} className="flex gap-2 flex-wrap">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.v-mart.cz/detske-zateplene-leginy/"
          required
          className="flex-1 min-w-[260px] text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-bg border border-border hover:border-primary text-text text-sm font-medium rounded-lg px-4 py-2.5 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
        >
          {loading ? 'Načítám…' : 'Načíst'}
        </button>
      </form>

      {products && products.length > 0 && (
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div>
            <div className="text-sm font-semibold text-text mb-2">
              Pořadí (první bude úplně nahoře)
            </div>
            <ol className="space-y-1.5">
              {products.map((p, idx) => (
                <li
                  key={p.guid}
                  className="flex items-center gap-2 bg-bg border border-border rounded-lg px-3 py-2"
                >
                  <span className="text-xs text-text-muted w-5 text-right">{idx + 1}.</span>
                  <span className="flex-1 min-w-0 text-sm text-text truncate">
                    {p.name}
                    {p.hidden && (
                      <span
                        className="ml-2 text-xs text-text-muted"
                        title="V kategorii je, ale na e-shopu se ve výpisu nezobrazuje (např. vyprodaný)"
                      >
                        (nezobrazuje se)
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-text-muted font-mono">{p.code ?? ''}</span>
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Posunout výš"
                    className="px-2 py-1 text-sm rounded hover:bg-surface disabled:opacity-30 cursor-pointer"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === products.length - 1}
                    aria-label="Posunout níž"
                    className="px-2 py-1 text-sm rounded hover:bg-surface disabled:opacity-30 cursor-pointer"
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <div className="text-sm font-semibold text-text mb-2">
              Posune se v {categories.size} kategoriích
            </div>
            <ul className="space-y-1 text-sm">
              {[...categories.entries()].map(([guid, c]) => {
                const res = results?.find((r) => r.guid === guid);
                return (
                  <li key={guid} className="flex items-center gap-2">
                    <span>{res ? (res.ok ? '✅' : '❌') : '•'}</span>
                    <span className="text-text">{c.name}</span>
                    <span className="text-text-muted text-xs">({c.count} ks)</span>
                    {res?.error && <span className="text-danger text-xs">{res.error}</span>}
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={apply}
              disabled={applying}
              className="mt-4 bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-5 py-2.5 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
            >
              {applying ? 'Posouvám…' : 'Posunout nahoru ve všech kategoriích'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
